<script setup>
import XlButton from '../xl/XlButton.vue';
import { BLOCKS, FALLBACK_NOTE, MERGE_FIELDS as FIELDS } from './mergeFieldSamples';

const props = defineProps({
  editor: {
    type: Object,
    default: null,
  },
});

// TipTap inserts at the current selection (replacing it if non-collapsed),
// which is the cursor for a collapsed selection — staff never type `{{...}}`.
const insert = (field) => {
  props.editor?.chain().focus().insertContent(`{{${field}}}`).run();
};

// A block is only drawn when its token is a paragraph of its own.
const insertBlock = (block) => {
  props.editor?.chain().focus().insertContent(`<p>{{${block}}}</p>`).run();
};
</script>

<template>
  <div class="merge-field-buttons">
    <div role="group" aria-labelledby="merge-field-label">
      <p id="merge-field-label" class="merge-field-buttons__label">Insert a personalised field</p>
      <div class="merge-field-buttons__row">
        <XlButton
          v-for="mf in FIELDS"
          :key="mf.field"
          :data-testid="`merge-field-${mf.field}`"
          :title="`If missing: ${mf.fallback}`"
          variant="ghost"
          size="sm"
          @click="insert(mf.field)"
        >
          <template #icon>
            <svg width="13" height="13" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M10 4.5v11M4.5 10h11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
            </svg>
          </template>
          {{ mf.label }}
        </XlButton>
      </div>
      <p class="merge-field-buttons__note">
        Click a button to drop that field into the message at your cursor — no code needed. It's replaced with each policyholder's real info when the message sends.
      </p>
      <p class="merge-field-buttons__note" data-testid="merge-field-fallbacks">{{ FALLBACK_NOTE }}</p>
    </div>

    <div role="group" aria-labelledby="merge-block-label">
      <p id="merge-block-label" class="merge-field-buttons__label">Insert a block</p>
      <div class="merge-field-buttons__row">
        <XlButton
          v-for="b in BLOCKS"
          :key="b.block"
          :data-testid="`merge-block-${b.block}`"
          variant="ghost"
          size="sm"
          @click="insertBlock(b.block)"
        >
          <template #icon>
            <svg width="13" height="13" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M10 4.5v11M4.5 10h11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
            </svg>
          </template>
          {{ b.label }}
        </XlButton>
      </div>
      <p class="merge-field-buttons__note">
        Goes in on a line of its own. The email shows the policy as a table and the buttons as buttons; the portal shows them as a list and links.
      </p>
    </div>
  </div>
</template>

<style scoped lang="scss">
.merge-field-buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__label {
    margin: 0 0 6px;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--xl-color-secondary-text);
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__note {
    margin: 7px 0 0;
    font-size: 12.5px;
    color: var(--xl-color-outline);
  }
}
</style>
